// Imported from the source module rather than the `@/services/api` barrel —
// this file is part of the api-barrel <-> services/auth import cycle (see
// account-api.ts for the details).
import { getApiClientErrorBody, getApiClientErrorDetail, getApiClientErrorStatus } from '@/services/api/errors';

type AuthResolutionErrorKind = 'rejected' | 'transient';

type AuthResolutionErrorOptions = {
  kind: AuthResolutionErrorKind;
  status: number | undefined;
  isMasqFetch: boolean;
  detail: string | undefined;
  cause: unknown;
};

/**
 * Typed failure of the auth resolution (`useAuthQuery`).
 *
 * The queryFn used to swallow every failure into `resolve(null)`, which erased
 * the error kind (401 vs 500 were indistinguishable downstream) and overwrote
 * the cached last-good auth data — leaving nothing to render the app from at
 * exactly the moment an in-place re-auth needs it. Throwing keeps react-query's
 * cached data intact and carries the classification:
 *
 * - `rejected` — the server refused the credentials (401/403). What this means
 *   depends on `isMasqFetch`: a rejected masq fetch says nothing about the
 *   admin's own session, while a rejected session-scoped fetch means the
 *   session is dead (expired/revoked) or the workspace is off-limits.
 * - `transient` — anything else (5xx, network failure). Not a session verdict;
 *   the session may well still be valid.
 */
export class AuthResolutionError extends Error {
  readonly kind: AuthResolutionErrorKind;
  /** HTTP status when the failure came from the API client; undefined for network-level failures. */
  readonly status: number | undefined;
  /** True when the failed fetch resolved a masqueraded account (`?masq_account_id`). */
  readonly isMasqFetch: boolean;
  /** Parsed error detail from the response body, when available. */
  readonly detail: string | undefined;

  constructor({ kind, status, isMasqFetch, detail, cause }: AuthResolutionErrorOptions) {
    super(`auth resolution ${kind}${status != null ? ` (${status})` : ''}${detail != null ? `: ${detail}` : ''}`, {
      cause,
    });
    this.name = 'AuthResolutionError';
    this.kind = kind;
    this.status = status;
    this.isMasqFetch = isMasqFetch;
    this.detail = detail;
  }
}

/** Classify a raw auth-resolution failure into a typed `AuthResolutionError`. */
export function toAuthResolutionError(error: unknown, isMasqFetch: boolean): AuthResolutionError {
  if (error instanceof AuthResolutionError) {
    return error;
  }
  const status = getApiClientErrorStatus(error);
  return new AuthResolutionError({
    kind: status === 401 || status === 403 ? 'rejected' : 'transient',
    status,
    isMasqFetch,
    detail: getApiClientErrorDetail(getApiClientErrorBody(error)),
    cause: error,
  });
}

/**
 * True when auth resolution failed because the server refused credentials —
 * session-scoped or masq. The authenticated layout routes any rejection into
 * its recovery chain (fallbacks, then the login redirect); transient failures
 * must NOT take that path (bouncing a possibly-valid session to /login on a
 * backend blip is the 5xx ping-pong flavor of FPS-2996).
 */
export function isAuthRejection(error: unknown): boolean {
  return error instanceof AuthResolutionError && error.kind === 'rejected';
}

/**
 * True when auth resolution failed because the server refused the *session's*
 * credentials — the signal for "this login can't continue" (redirect to login
 * today; the in-place re-auth prompt once it exists). Excludes masq-fetch
 * rejections: the underlying login may still be valid there, and bouncing home
 * is the intended recovery for a bad masq target.
 */
export function isSessionRejection(error: unknown): boolean {
  return error instanceof AuthResolutionError && error.kind === 'rejected' && !error.isMasqFetch;
}

/**
 * True when the session's own credentials were rejected as *expired* (401) —
 * the only rejection an in-place re-auth can fix. A 403 means the login is
 * valid but not authorized for what it asked (workspace off-limits, expired
 * masquerade); re-entering credentials wouldn't help, so those fall through to
 * the login redirect / workspace-mismatch recovery instead of the modal.
 */
export function isExpiredSessionRejection(error: unknown): boolean {
  return isSessionRejection(error) && error instanceof AuthResolutionError && error.status === 401;
}
